import { supabase } from '../lib/supabase'

export type AssignmentSource = 'property_default' | 'custom' | 'unassigned'

export interface AssignmentDefaults {
  assigned_crew_id: string | null
  assigned_employee_id: string | null
}

/** Resolve effective crew/employee for a contract (+ optional visit group). */
export async function resolveJobAssignmentDefaults(
  contractId: string,
  visitGroupId?: string | null
): Promise<AssignmentDefaults> {
  const { data, error } = await supabase.rpc('resolve_job_assignment_defaults', {
    p_contract_id: contractId,
    p_visit_group_id: visitGroupId || null,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return {
    assigned_crew_id: row?.assigned_crew_id || null,
    assigned_employee_id: row?.assigned_employee_id || null,
  }
}

/** Resolve property-level defaults (one-time jobs without a contract). */
export async function resolvePropertyAssignmentDefaults(
  propertyId: string
): Promise<AssignmentDefaults> {
  const { data, error } = await supabase.rpc('resolve_property_assignment_defaults', {
    p_property_id: propertyId,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return {
    assigned_crew_id: row?.assigned_crew_id || null,
    assigned_employee_id: row?.assigned_employee_id || null,
  }
}

/**
 * Prefer contract resolution when contractId is set; otherwise property.
 * Falls back to client-side join if RPCs are not deployed yet.
 */
export async function resolveAssignmentDefaultsForJob(opts: {
  contractId?: string | null
  propertyId?: string | null
  visitGroupId?: string | null
}): Promise<AssignmentDefaults> {
  const empty: AssignmentDefaults = { assigned_crew_id: null, assigned_employee_id: null }
  try {
    if (opts.contractId) {
      if (opts.propertyId && !opts.visitGroupId) {
        const { data: contract } = await supabase
          .from('contracts')
          .select('assignment_source')
          .eq('id', opts.contractId)
          .maybeSingle()
        if (!contract || contract.assignment_source === 'property_default') {
          return await resolvePropertyAssignmentDefaults(opts.propertyId)
        }
        if (contract.assignment_source === 'unassigned') return empty
      }
      return await resolveJobAssignmentDefaults(opts.contractId, opts.visitGroupId)
    }
    if (opts.propertyId) {
      return await resolvePropertyAssignmentDefaults(opts.propertyId)
    }
  } catch (err: any) {
    if (err?.code !== '42883' && !/function .* does not exist/i.test(err?.message || '')) {
      throw err
    }
    return resolveAssignmentDefaultsClientSide(opts)
  }
  return empty
}

async function resolveAssignmentDefaultsClientSide(opts: {
  contractId?: string | null
  propertyId?: string | null
  visitGroupId?: string | null
}): Promise<AssignmentDefaults> {
  let baseCrew: string | null = null
  let baseEmp: string | null = null

  if (opts.contractId) {
    const { data: contract } = await supabase
      .from('contracts')
      .select(
        'assignment_source, default_assigned_crew_id, default_assigned_employee_id, property_id'
      )
      .eq('id', opts.contractId)
      .maybeSingle()

    if (contract) {
      if (contract.assignment_source === 'custom') {
        baseCrew = contract.default_assigned_crew_id || null
        baseEmp = contract.default_assigned_employee_id || null
      } else if (
        contract.assignment_source === 'property_default' &&
        (opts.propertyId || contract.property_id)
      ) {
        const { data: prop } = await supabase
          .from('properties')
          .select('default_assigned_crew_id, default_assigned_employee_id')
          .eq('id', opts.propertyId || contract.property_id)
          .maybeSingle()
        baseCrew = prop?.default_assigned_crew_id || null
        baseEmp = prop?.default_assigned_employee_id || null
      }
    }
  } else if (opts.propertyId) {
    const { data: prop } = await supabase
      .from('properties')
      .select('default_assigned_crew_id, default_assigned_employee_id')
      .eq('id', opts.propertyId)
      .maybeSingle()
    baseCrew = prop?.default_assigned_crew_id || null
    baseEmp = prop?.default_assigned_employee_id || null
  }

  if (opts.visitGroupId) {
    const { data: vg } = await supabase
      .from('contract_visit_groups')
      .select('assigned_crew_id, assigned_employee_id')
      .eq('id', opts.visitGroupId)
      .maybeSingle()
    return {
      assigned_crew_id: vg?.assigned_crew_id || baseCrew,
      assigned_employee_id: vg?.assigned_employee_id || baseEmp,
    }
  }

  return { assigned_crew_id: baseCrew, assigned_employee_id: baseEmp }
}

/** Overwrite future open jobs for a contract (soft-fail friendly for callers). */
export async function reapplyAssignmentDefaultsForContract(
  contractId: string
): Promise<number> {
  const { data, error } = await supabase.rpc('reapply_assignment_defaults_for_contract', {
    p_contract_id: contractId,
  })
  if (error) throw error
  return typeof data === 'number' ? data : 0
}

/** Overwrite future open jobs for contracts inheriting this property's defaults. */
export async function reapplyAssignmentDefaultsForProperty(
  propertyId: string
): Promise<number> {
  const { data, error } = await supabase.rpc('reapply_assignment_defaults_for_property', {
    p_property_id: propertyId,
  })
  if (error) throw error
  return typeof data === 'number' ? data : 0
}

/** Insert job_assignments rows for a newly created job (mirrors assignJob*). */
export async function recordJobAssignmentsOnCreate(
  jobId: string,
  crewId: string | null | undefined,
  employeeId: string | null | undefined
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  const rows: Array<{ job_id: string; crew_id?: string; employee_id?: string; assigned_by: string | null }> =
    []
  if (crewId) {
    rows.push({ job_id: jobId, crew_id: crewId, assigned_by: user?.id || null })
  }
  if (employeeId) {
    rows.push({ job_id: jobId, employee_id: employeeId, assigned_by: user?.id || null })
  }
  if (rows.length === 0) return
  const { error } = await supabase.from('job_assignments').insert(rows)
  if (error) throw error
}
